/**
 * React Aria documentation
 * Disclosure: https://react-spectrum.adobe.com/react-aria/Disclosure.html
 * DisclosureGroup: https://react-spectrum.adobe.com/react-aria/DisclosureGroup.html
 */
import React, { useContext } from 'react';
import {
  Disclosure as AriaDisclosure,
  DisclosureGroup as AriaDisclosureGroup,
  type DisclosureProps as AriaDisclosureProps,
  type DisclosureGroupProps as AriaDisclosureGroupProps,
  DisclosurePanel as AriaDisclosurePanel,
  type DisclosurePanelProps as AriaDisclosurePanelProps,
  composeRenderProps,
  Heading,
  type HeadingProps,
  Button,
  DisclosureStateContext,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';
import { composeTailwindRenderProps, focusRing } from './component-utils';
import { DisclosureGroupStateContext } from 'react-aria-components';
import { LabAddIcon } from 'components/camperlab/icons/add';
import { LabMinusIcon } from 'components/camperlab/icons/minus';

const disclosure = tv({
  base: 'group min-w-64 text-copy',
  // variants: {
  //   isInGroup: {
  //     true: 'border-0 border-b',
  //   },
  // },
});

const disclosureButton = tv({
  extend: focusRing,
  base: 'flex w-full cursor-default items-center justify-between gap-2 text-start text-sm uppercase lg:text-xs',
  variants: {
    isDisabled: {
      true: 'text-disabled forced-colors:text-[GrayText]',
    },
    isInGroup: {
      true: '-outline-offset-2',
    },
  },
});

const disclosureIcon = tv({
  base: 'text-copy transition duration-200 ease-in-out',
  variants: {
    // isExpanded: {
    //   true: 'rotate-90 transform',
    // },
    isDisabled: {
      true: 'text-disabled forced-colors:text-[GrayText]',
    },
  },
});

export type DisclosureProps = AriaDisclosureProps & {
  children: React.ReactNode;
};

export function Disclosure({ children, ...props }: DisclosureProps) {
  // const isInGroup = useContext(DisclosureGroupStateContext) !== null;
  return (
    <AriaDisclosure {...props} className={composeRenderProps(props.className, (className, renderProps) => disclosure({ ...renderProps, className }))}>
      {children}
    </AriaDisclosure>
  );
}

export type DisclosureHeaderProps = HeadingProps & {
  children: React.ReactNode;
};

export function DisclosureHeader({ children, ...rest }: DisclosureHeaderProps) {
  const { isExpanded } = useContext(DisclosureStateContext)!;
  const isInGroup = useContext(DisclosureGroupStateContext) !== null;
  return (
    <Heading {...rest} className="mb-0">
      <Button slot="trigger" className={(renderProps) => disclosureButton({ ...renderProps, isInGroup })}>
        {({ isDisabled }) => (
          <>
            {children}
            {isExpanded ?
              <LabMinusIcon aria-hidden className={disclosureIcon({ isDisabled })} />
            : <LabAddIcon aria-hidden className={disclosureIcon({ isDisabled })} />}
          </>
        )}
      </Button>
    </Heading>
  );
}

export interface DisclosurePanelProps extends AriaDisclosurePanelProps {
  children: React.ReactNode;
}

export function DisclosurePanel({ children, ...props }: DisclosurePanelProps) {
  return (
    <AriaDisclosurePanel {...props} className={composeTailwindRenderProps(props.className, 'group-data-[expanded]:pb-5 group-data-[expanded]:pt-2.5')}>
      {children}
    </AriaDisclosurePanel>
  );
}

export interface DisclosureGroupProps extends AriaDisclosureGroupProps {
  children: React.ReactNode;
}

export function DisclosureGroup({ children, ...props }: DisclosureGroupProps) {
  return (
    <AriaDisclosureGroup {...props} className={composeTailwindRenderProps(props.className, 'flex flex-col gap-1.5 lab:gap-0')}>
      {children}
    </AriaDisclosureGroup>
  );
}
